import React, { useState, useEffect } from "react";
import {
  MdEdit,
  MdDelete,
  MdSearch,
  MdBusiness,
  MdAdd,
  MdPeople,
  MdArchive
} from "react-icons/md";
import {
  getAllCompanies,
  updateCompany,
  deleteCompany,
  Company
} from "../../utils/api/endpoints/companies";
import toast from "react-hot-toast";
import usePageTitle from "../../utils/hooks/usePageTitle";
import CompanyEditModal from "../../components/companies/CompanyEditModal";
import CompanyCreateModal from "../../components/companies/CompanyCreateModal";
import ManageUsersModal from "../../components/companies/ManageUsersModal";

const CompanyManagement = () => {
  usePageTitle("Company Management");

  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [subscriptionFilter, setSubscriptionFilter] = useState<string>("all");

  // Modal state
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isManageUsersModalOpen, setIsManageUsersModalOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);

  // Fetch ALL companies (no pagination)
  const fetchCompanies = async () => {
    setLoading(true);
    const result = await getAllCompanies(1, 9999, searchQuery, true); // Get all companies
    if (result) {
      setCompanies(result.companies);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchCompanies();
  }, [searchQuery]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    fetchCompanies();
  };

  const handleEdit = (company: Company) => {
    setSelectedCompany(company);
    setIsEditModalOpen(true);
  };

  const handleManageUsers = (company: Company) => {
    setSelectedCompany(company);
    setIsManageUsersModalOpen(true);
  };

  const handleDelete = async (company: Company) => {
    if (company.users && company.users.length > 0) {
      toast.error("Cannot delete company with associated users");
      return;
    }

    if (
      !window.confirm(
        `Are you sure you want to delete "${company.company_name}"? This action cannot be undone.`
      )
    ) {
      return;
    }

    const success = await deleteCompany(company.company_id);
    if (success) {
      fetchCompanies();
    }
  };

  const handleSaveCompany = async (
    companyId: number,
    companyName: string,
    logoFile: File | null,
    subscriptionType: any,
    quickRunsQuota: number | null,
    comprehensiveRunsQuota: number | null,
    quickRunDuration: number | null,
    comprehensiveRunDuration: number | null,
    isArchived: boolean | null,
    subscriptionStartDate: string | null,
    subscriptionEndDate: string | null
  ) => {
    const result = await updateCompany(
      companyId,
      companyName,
      logoFile,
      subscriptionType,
      quickRunsQuota,
      comprehensiveRunsQuota,
      quickRunDuration,
      comprehensiveRunDuration,
      isArchived,
      subscriptionStartDate,
      subscriptionEndDate
    );
    if (result) {
      setIsEditModalOpen(false);
      setSelectedCompany(null);
      fetchCompanies();
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 px-4 py-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Company Management</h1>
        <p className="mt-2 text-sm text-gray-600">
          Manage all companies and their associated business users
        </p>
      </div>

      {/* Search, Filter, Stats, and Actions */}
      <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        {/* Search and Filter */}
        <div className="flex flex-1 gap-3">
          <form onSubmit={handleSearch} className="flex-1 max-w-md">
            <div className="relative">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search companies..."
                className="w-full rounded-lg border border-gray-300 py-2 pl-10 pr-4 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              <MdSearch className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
            </div>
          </form>

          {/* Subscription Filter */}
          <select
            value={subscriptionFilter}
            onChange={(e) => setSubscriptionFilter(e.target.value)}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            <option value="all">All Subscriptions</option>
            <option value="monthly">Monthly</option>
            <option value="quarterly">Quarterly</option>
            <option value="half_yearly">Half Yearly</option>
            <option value="yearly">Yearly</option>
            <option value="pilot">Pilot</option>
            <option value="none">No Subscription</option>
          </select>
        </div>

        {/* Stats and Create Button */}
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <MdBusiness className="h-5 w-5" />
            <span className="font-medium">
              {companies.filter(company => {
                if (subscriptionFilter === "all") return true;
                if (subscriptionFilter === "none") return !company.subscription_type;
                return company.subscription_type === subscriptionFilter;
              }).length} Companies
            </span>
          </div>
          <button
            onClick={() => setIsCreateModalOpen(true)}
            className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
          >
            <MdAdd className="h-5 w-5" />
            Create Company
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  Company
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  Subscription
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  Business Users
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  Created
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {loading ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center">
                    <div className="flex items-center justify-center">
                      <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-500 border-t-transparent"></div>
                    </div>
                  </td>
                </tr>
              ) : (() => {
                const filteredCompanies = companies.filter(company => {
                  if (subscriptionFilter === "all") return true;
                  if (subscriptionFilter === "none") return !company.subscription_type;
                  return company.subscription_type === subscriptionFilter;
                });

                if (filteredCompanies.length === 0) {
                  return (
                    <tr>
                      <td colSpan={5} className="px-6 py-12 text-center">
                        <MdBusiness className="mx-auto h-12 w-12 text-gray-400" />
                        <p className="mt-2 text-sm text-gray-500">
                          {searchQuery || subscriptionFilter !== "all"
                            ? "No companies found matching your filters"
                            : "No companies yet"}
                        </p>
                      </td>
                    </tr>
                  );
                }

                return filteredCompanies.map((company) => (
                  <tr key={company.company_id} className={`hover:bg-gray-50 ${company.is_archived ? 'bg-orange-50' : ''}`}>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        {company.company_logo ? (
                          <img
                            src={company.company_logo}
                            alt={company.company_name}
                            className="h-10 w-10 rounded-lg object-cover"
                          />
                        ) : (
                          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-100 text-blue-600">
                            <MdBusiness className="h-6 w-6" />
                          </div>
                        )}
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-gray-900">
                              {company.company_name}
                            </span>
                            {company.is_archived && (
                              <span className="inline-flex items-center gap-1 rounded-full bg-orange-500 px-2 py-0.5 text-xs font-semibold text-white">
                                <MdArchive className="h-3 w-3" />
                                Archived
                              </span>
                            )}
                          </div>
                          <div className="text-sm text-gray-500">
                            ID: {company.company_id}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {company.subscription_type ? (
                        <span className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${company.subscription_type === "yearly" ? "bg-purple-100 text-purple-800" :
                            company.subscription_type === "half_yearly" ? "bg-blue-100 text-blue-800" :
                              company.subscription_type === "quarterly" ? "bg-green-100 text-green-800" :
                                company.subscription_type === "monthly" ? "bg-yellow-100 text-yellow-800" :
                                  company.subscription_type === "pilot" ? "bg-orange-100 text-orange-800" :
                                    "bg-gray-100 text-gray-800"
                          }`}>
                          {company.subscription_type === "half_yearly" ? "Half Yearly" :
                            company.subscription_type.charAt(0).toUpperCase() + company.subscription_type.slice(1)}
                        </span>
                      ) : (
                        <span className="text-sm text-gray-400">No subscription</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">
                        {company.users && company.users.length > 0 ? (
                          <div>
                            <div className="font-medium">
                              {company.users.length} user
                              {company.users.length !== 1 ? "s" : ""}
                            </div>
                            {/* <div className="mt-1 space-y-1">
                              {company.users.map((user) => (
                                <div
                                  key={user.user_id}
                                  className="text-xs text-gray-500"
                                >
                                  {user.username} ({user.email})
                                </div>
                              ))}
                            </div> */}
                          </div>
                        ) : (
                          <span className="text-gray-400">No users</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {company.created_at
                        ? new Date(company.created_at).toLocaleDateString()
                        : "N/A"}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleManageUsers(company)}
                          className="rounded-lg p-2 text-green-600 hover:bg-green-50 transition"
                          title="Manage users"
                        >
                          <MdPeople className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => handleEdit(company)}
                          className="rounded-lg p-2 text-blue-600 hover:bg-blue-50 transition"
                          title="Edit company"
                        >
                          <MdEdit className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => handleDelete(company)}
                          className="rounded-lg p-2 text-red-600 hover:bg-red-50 transition disabled:opacity-50 disabled:cursor-not-allowed"
                          title={
                            company.users && company.users.length > 0
                              ? "Cannot delete company with users"
                              : "Delete company"
                          }
                          disabled={company.users && company.users.length > 0}
                        >
                          <MdDelete className="h-5 w-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ));
              })()}
            </tbody>
          </table>
        </div>
      </div>

      {/* Edit Modal */}
      {isEditModalOpen && selectedCompany && (
        <CompanyEditModal
          company={selectedCompany}
          onClose={() => {
            setIsEditModalOpen(false);
            setSelectedCompany(null);
          }}
          onSave={handleSaveCompany}
        />
      )}

      {/* Create Modal */}
      {isCreateModalOpen && (
        <CompanyCreateModal
          onClose={() => setIsCreateModalOpen(false)}
          onSuccess={() => {
            setIsCreateModalOpen(false);
            fetchCompanies();
          }}
        />
      )}

      {/* Manage Users Modal */}
      {isManageUsersModalOpen && selectedCompany && (
        <ManageUsersModal
          company={selectedCompany}
          onClose={() => {
            setIsManageUsersModalOpen(false);
            setSelectedCompany(null);
          }}
          onSuccess={() => {
            fetchCompanies();
          }}
        />
      )}
    </div>
  );
};

export default CompanyManagement;

